import { formatDateKey, getMonthDays } from '../../../components/calendar/calendar-utils';
import type { DayStatus } from '../../../components/calendar/weel-day-row';

const VALID_DAY_STATUSES: Set<string> = new Set<DayStatus>([
  'complete',
  'partial',
  'nonCompliant',
  'missing',
  'notRequired',
  'future',
]);

export function parseDayStatus(raw: string | undefined): DayStatus {
  if (raw && VALID_DAY_STATUSES.has(raw)) {
    return raw as DayStatus;
  }
  return 'missing';
}

export function getMonthDateRange(month: Date): { fromDate: string; toDate: string } {
  const year = month.getFullYear();
  const monthIndex = month.getMonth();
  const firstDay = new Date(year, monthIndex, 1);
  const lastDay = new Date(year, monthIndex + 1, 0);
  return { fromDate: formatDateKey(firstDay), toDate: formatDateKey(lastDay) };
}

export interface SimpleCalendarData {
  calendar: Record<string, string>;
}

export interface OilCalendarData {
  days: Record<string, { status: string }>;
}

export function mapWeekStatuses(
  weekDays: Date[],
  data: SimpleCalendarData | undefined,
): Map<number, DayStatus> {
  const statusMap = new Map<number, DayStatus>();
  for (let i = 0; i < weekDays.length; i++) {
    const dayKey = formatDateKey(weekDays[i]);
    const status = parseDayStatus(data?.calendar[dayKey]);
    statusMap.set(i, status);
  }
  return statusMap;
}

export function mapMonthStatuses(
  month: Date,
  data: SimpleCalendarData | undefined,
): Map<string, DayStatus> {
  const statusMap = new Map<string, DayStatus>();
  for (const dayKey of getMonthDays(month).map(formatDateKey)) {
    statusMap.set(dayKey, parseDayStatus(data?.calendar[dayKey]));
  }
  return statusMap;
}

export function mapOilWeekStatuses(
  weekDays: Date[],
  data: OilCalendarData | undefined,
): Map<number, DayStatus> {
  const statusMap = new Map<number, DayStatus>();
  for (let i = 0; i < weekDays.length; i++) {
    const dayKey = formatDateKey(weekDays[i]);
    const status = parseDayStatus(data?.days[dayKey]?.status);
    statusMap.set(i, status);
  }
  return statusMap;
}

export function mapOilMonthStatuses(
  month: Date,
  data: OilCalendarData | undefined,
): Map<string, DayStatus> {
  const statusMap = new Map<string, DayStatus>();
  for (const dayKey of getMonthDays(month).map(formatDateKey)) {
    statusMap.set(dayKey, parseDayStatus(data?.days[dayKey]?.status));
  }
  return statusMap;
}

// Only statuses that indicate a check was actually performed or required count as "scheduled".
const SCHEDULED_STATUSES: Set<string> = new Set(['complete', 'partial', 'nonCompliant']);

export function deriveScheduledDaysFromStatuses(dayStatuses: Map<string, DayStatus>): Set<string> {
  const scheduled = new Set<string>();
  for (const [dayKey, status] of dayStatuses) {
    if (SCHEDULED_STATUSES.has(status)) {
      scheduled.add(dayKey);
    }
  }
  return scheduled;
}
